import * as React from 'react';
import { useEffect, useState } from 'react';

import { getGroupInfo, getGroupMembers, listRoles } from 'src/api/permissions';

import {
  Button,
  ButtonDropdown,
  CollectionPreferences,
  CollectionPreferencesProps,
  ColumnLayout,
  Container,
  Flashbar,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  Tabs,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import { useCollection } from '@amzn/awsui-collection-hooks';

import { isValidUser } from 'src/commons/validationUtils';
import { CopiableText, getGroupingMechanismType, getPhoneToolLink, groupingMechanismIdUrl } from 'src/commons/common';
import { defaultWrapLinesPreference, paginationLabels } from 'src/commons/tables';
import { EmptyState } from 'src/commons/EmptyState';
import { TABLE_CONTENT_TYPE } from 'src/commons/constants';
import { ManageRoles } from 'src/components/groups/manageRoles';
import { PageHeader } from 'src/components/common/PageHeader';
import { ResourceNotFound } from 'src/commons/ResourceNotFound';
import { CommonPageProps } from '../common/CommonPageProps';
import { AddGroupMembersModal } from 'src/components/groups/addGroupMembersModal';
import { ConfirmGroupCreationModal } from 'src/components/groups/confirmGroupCreationModal';
import { UpdateGroupModal } from 'src/components/groups/updateGroupModal';

export interface MyGroupsProps extends CommonPageProps {}

const MyGroups = (props: MyGroupsProps) => {
  const [groupId, setGroupId] = useState<string>(undefined);
  const [isOwner, setIsOwner] = useState<boolean>(false);

  const [activeGroupInfo, setActiveGroupInfo] = useState<any>(undefined);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [, setGroupMemberIds] = useState<any[]>([]);
  const [loadingGroupMembers, setLoadingGroupMembers] = useState<boolean>(true);
  const [loadingGroupInfo, setLoadingGroupInfo] = useState<boolean>(true);

  const [updateGroupModalVisible, setUpdateGroupModalVisible] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [tableMessage, setTableMessage] = useState<string>('No group members');

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 10,
  });

  const [roleOptions, setRoleOptions] = useState(undefined);
  const [addMembersModalVisible, setAddMembersModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'userId',
      header: 'Member ID',
      cell: (item) => {
        if (isValidUser(item.userId)) return getPhoneToolLink(item.userId);
        return item.userId;
      },
      minWidth: 200,
      sortingField: 'userId',
    },
    {
      id: 'updatedDate',
      header: 'Last updated',
      cell: (item) => item.updatedDate,
      minWidth: 200,
      sortingField: 'updatedDate',
    },
  ];

  const readableCustomerTypeMap = {
    DATAPROVIDER_AND_DATACONSUMER: 'Data provider and consumer',
    DATAPROVIDER: 'Data provider',
    DATACONSUMER: 'Data consumer',
  };

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);

    const groupId = props.match.params.id ? props.match.params.id : props.activeGroup;

    setGroupId(groupId);
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [props.activeGroup, groupId]);

  const handleRefresh = async () => {
    if (!groupId) return;

    if (props.match.params.id === undefined || props.match.params.id === props.activeGroup) {
      setIsOwner(true);
    }

    await fetchGroupInfo();
    await fetchGroupMembers();
  };

  const fetchGroupInfo = async () => {
    setLoadingGroupInfo(true);

    try {
      const activeGroupInfo = await getGroupInfo({
        groupId,
      });
      setActiveGroupInfo(activeGroupInfo);

      const listRolesResult = await listRoles({ ownerId: groupId });
      if (listRolesResult && listRolesResult['roles']) {
        const roleOptions = [];
        for (let role of listRolesResult['roles']) {
          roleOptions.push({
            label: role.roleName,
            value: role.roleId,
            description: role.description,
          });
        }
        setRoleOptions(roleOptions);
      }
    } catch (err) {
      console.error('Failed to get group info.', err);
    }

    setLoadingGroupInfo(false);
  };

  const fetchGroupMembers = async () => {
    setLoadingGroupMembers(true);
    try {
      const groupMembers = await getGroupMembers({
        groupId,
      });
      const existingUserIds = [];
      groupMembers.groupMembers.forEach((member) => existingUserIds.push(member['userId']));

      setGroupMembers(groupMembers.groupMembers);
      setGroupMemberIds(existingUserIds);
    } catch (err) {
      setTableMessage('Unable to load group members.');
    }
    setLoadingGroupMembers(false);
  };

  const getButtonList = () => {
    if (!activeGroupInfo) return [];

    let buttonsList = [];
    const refreshButton = {
      text: '',
      icon: 'refresh',
      onItemClick: handleRefresh,
    };
    const addUserButton = {
      text: 'Add IAM principals',
      variant: 'normal',
      onItemClick: () => {
        setAddMembersModalVisible(true);
      },
    };

    const isValidRoleOptions = roleOptions && roleOptions.length;
    if (isOwner && isValidRoleOptions) buttonsList.push(addUserButton);

    buttonsList.push(refreshButton);
    return buttonsList;
  };

  const setNotification = (header, message) => {
    if (header === '') {
      setNotifications([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    } else {
      setNotifications([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const formatCTI = () => {
    if (
      activeGroupInfo?.primaryCTI == undefined ||
      activeGroupInfo?.primaryCTI.category == undefined ||
      activeGroupInfo?.primaryCTI.type == undefined ||
      activeGroupInfo?.primaryCTI.item == undefined
    ) {
      return 'N/A';
    }
    return (
      activeGroupInfo?.primaryCTI.category +
      ' / ' +
      activeGroupInfo?.primaryCTI.type +
      ' / ' +
      activeGroupInfo?.primaryCTI.item
    );
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(groupMembers, {
    filtering: {
      noMatch: '',
      empty: <EmptyState title={tableMessage} subtitle={'No group members to display.'} />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {},
    propertyFiltering: {
      filteringProperties: [],
    },
  });

  const getGroupDetails = () => (
    <Container
      header={
        <Header
          variant='h2'
          actions={
            <SpaceBetween direction='horizontal' size='xs'>
              <ButtonDropdown
                variant='normal'
                items={[
                  {
                    text: 'Create new group',
                    id: 'createnew',
                  },
                ]}
                onItemClick={() => {
                  setWarningModalVisible(true);
                }}
              >
                Actions
              </ButtonDropdown>
              {isOwner && (
                <Button
                  variant='normal'
                  onClick={() => {
                    setUpdateGroupModalVisible(true);
                  }}
                >
                  {' '}
                  Edit{' '}
                </Button>
              )}
            </SpaceBetween>
          }
        >
          {' '}
          Group details{' '}
        </Header>
      }
    >
      <ColumnLayout columns={3} borders='horizontal'>
        <CopiableText name='Team name' value={activeGroupInfo?.teamInfo.Name} />
        <CopiableText name='Team email' value={activeGroupInfo?.teamInfo.DistributionList} />
        {activeGroupInfo?.groupingMechanismId != null ? (
          <CopiableText
            name={`Grouping ID (${getGroupingMechanismType(activeGroupInfo?.groupingMechanismId)})`}
            value={activeGroupInfo?.groupingMechanismId}
            url={groupingMechanismIdUrl(activeGroupInfo?.groupingMechanismId)}
            copiable={false}
            external={true}
          />
        ) : (
          <CopiableText
            name={`Grouping ID`}
            value={'Please provide a POSIX or Teams group ID.'}
            copiable={false}
            external={true}
          />
        )}
        <CopiableText
          name={`Team wiki`}
          value={activeGroupInfo?.teamInfo.Wiki}
          url={activeGroupInfo?.teamInfo.Wiki}
          copiable={false}
          external={true}
        />
        <CopiableText
          name='Customer type'
          value={readableCustomerTypeMap[activeGroupInfo?.teamInfo.CustomerType]}
          copiable={false}
        />
        <CopiableText
          name='Data access and processing'
          value={activeGroupInfo?.teamInfo.DataAccessAndProcessing}
          copiable={false}
        />

        <CopiableText name='Last updated date' value={activeGroupInfo?.updatedDate} />
        <CopiableText name={`Use case`} value={activeGroupInfo?.teamInfo.UseCase} copiable={false} />
        <CopiableText name={`Existing use case`} value={activeGroupInfo?.teamInfo.ExistingUseCase} copiable={false} />
        <CopiableText name='CTI' value={formatCTI()} />
      </ColumnLayout>
    </Container>
  );

  const getGroupMembersTable = () => (
    <Table
      {...collectionProps}
      loadingText='Loading group members...'
      loading={loadingGroupMembers}
      columnDefinitions={columnDefinitions}
      items={items}
      wrapLines={false}
      resizableColumns={true}
      empty={
        <EmptyState title={'No match'} subtitle={"No users found or you don't have permissions to see the users"} />
      }
      header={
        <>
          <PageHeader buttons={getButtonList()} counter={`(${groupMembers.length})`} header={'Members'} />
        </>
      }
      pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
      filter={
        <TextFilter
          {...filterProps}
          filteringAriaLabel='Filter resources'
          filteringPlaceholder='Find resources'
          countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
        />
      }
      preferences={
        <CollectionPreferences
          title={'Preferences'}
          confirmLabel={'Confirm'}
          cancelLabel={'Cancel'}
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={{
            title: 'Page size',
            options: [
              { value: 10, label: '10 items' },
              { value: 20, label: '20 items' },
              { value: 30, label: '30 items' },
            ],
          }}
          wrapLinesPreference={defaultWrapLinesPreference}
        />
      }
    />
  );

  const getGroupPageTabs = () => {
    const tabs = [
      {
        label: 'Details',
        id: 'details',
        content: getGroupDetails(),
      },
      {
        label: 'Members',
        id: 'members',
        content: getGroupMembersTable(),
      },
      {
        label: 'Roles',
        id: 'roles',
        content: (
          <ManageRoles ownerId={groupId} roleMapping={activeGroupInfo ? activeGroupInfo.roleMapping : undefined} />
        ),
      },
    ];

    return <Tabs tabs={tabs} />;
  };

  if (!loadingGroupInfo && !activeGroupInfo) {
    return (
      <ResourceNotFound
        title={'Group not found'}
        subtitle={
          'The given group is not valid, or you do not have permission to view it. Please check the URL for mistakes and try again.'
        }
      />
    );
  }

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      {notifications?.length > 0 && <Flashbar items={notifications} />}
      <Header variant={'h1'}>{groupId}</Header>

      {getGroupPageTabs()}

      {addMembersModalVisible && (
        <AddGroupMembersModal
          visible={addMembersModalVisible}
          setVisible={setAddMembersModalVisible}
          setNotifications={setNotification}
          activeGroupInfo={activeGroupInfo}
          groupId={groupId}
          groupMembers={groupMembers}
          roleOptions={roleOptions}
        />
      )}
      {warningModalVisible && (
        <ConfirmGroupCreationModal
          visible={warningModalVisible}
          setVisible={setWarningModalVisible}
          setRedirect={props.setRedirect}
        />
      )}
      {updateGroupModalVisible && (
        <UpdateGroupModal
          visible={updateGroupModalVisible}
          setVisible={setUpdateGroupModalVisible}
          setRedirect={props.setRedirect}
          setNotifications={setNotification}
          groupId={groupId}
          activeGroupInfo={activeGroupInfo}
        />
      )}
    </SpaceBetween>
  );
};

export default MyGroups;
